import { Button, Icon, Table, Tooltip } from '@zoom/components';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { EllipsisContent } from '../../components/EllipsisContent';
import { SortedBulkLabels } from '../../components/SortedBulkLabels';
import { BottomPanelHeaderProps, useBottomPanel } from '../../components/layout/BottomPanel';
import { BOTTOM_PANEL_CLOSE_BUTTON } from '../../components/layout/constants';
import { AdherenceHistoryItem, Team } from '../../graphql/components';
import { formatSecondsToReadableForm } from '../../utils/dateRange';
import { formatNameOfPerson, nameOf } from '../../utils/helpers';
import { AdherenceHistoryBadge } from './AdherenceHistoryBadge';
import { AGENT_DETAILS_CONTAINER_TAG, getAgentDetailsItemTag } from './constants';

type AdherenceHistoryBottomPanelHeaderProps = {
  agentData: AdherenceHistoryItem;
  isOneDaySelected?: boolean;
  teams: Record<string, Team>;
} & BottomPanelHeaderProps;

export const AdherenceHistoryBottomPanelHeader: React.FC<AdherenceHistoryBottomPanelHeaderProps> = ({
  agentData: {
    agentName,
    agentSurname,
    conformancePercentage,
    earlyLeavesCount,
    inAdherencePercentage,
    lateArrivalsCount,
    outOfAdhEventsCount,
    outOfAdhTimeTotal,
    scheduledTime,
    scheduledWorkingTime,
    teamIds,
  },
  isOneDaySelected,
  onClose,
  teams,
}) => {
  const { closeBottomPanel } = useBottomPanel();
  const { t } = useTranslation();
  const { locale } = useAuth();

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    closeBottomPanel();
  };

  return (
    <div className="bottom-panel__header position-relative">
      <Table
        data-test-id={AGENT_DETAILS_CONTAINER_TAG}
        borderless
        className="adherence-history__agent-details"
        fixedLayout
        withContainerPadding
      >
        <tbody>
          <tr>
            <EllipsisContent
              as="td"
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('agentSurname'))}
              className="agent-details__name"
            >
              {agentSurname === 'Unknown' && agentName === 'Unknown'
                ? t('person.unknown')
                : formatNameOfPerson(agentName, agentSurname)}
            </EllipsisContent>
            <td className="col-wrap" data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('teamIds'))}>
              <SortedBulkLabels
                items={teamIds.map((teamId) => teams[teamId]).filter(Boolean)}
                label={t('team.team', { count: 2 })}
              />
            </td>
            <td data-test-id={getAgentDetailsItemTag('scheduledTime')} className="text-center">
              {formatSecondsToReadableForm(scheduledTime, t)}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('scheduledWorkingTime'))}
              className="text-center"
            >
              {formatSecondsToReadableForm(scheduledWorkingTime, t)}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('outOfAdhTimeTotal'))}
              className="text-center"
            >
              {formatSecondsToReadableForm(outOfAdhTimeTotal, t)}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('outOfAdhEventsCount'))}
              className="text-center"
            >
              <AdherenceHistoryBadge kind="info">{outOfAdhEventsCount}</AdherenceHistoryBadge>
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('inAdherencePercentage'))}
              className="text-center"
            >
              {`${(inAdherencePercentage / 100).toLocaleString(locale, {
                maximumFractionDigits: 2,
                style: 'percent',
              })}`}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('conformancePercentage'))}
              className="text-center"
            >
              {`${(conformancePercentage / 100).toLocaleString(locale, {
                maximumFractionDigits: 2,
                style: 'percent',
              })}`}
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('lateArrivalsCount'))}
              className="text-center"
            >
              <AdherenceHistoryBadge displayValueAsWord={isOneDaySelected} kind="danger">
                {lateArrivalsCount}
              </AdherenceHistoryBadge>
            </td>
            <td
              data-test-id={getAgentDetailsItemTag(nameOf<AdherenceHistoryItem>('earlyLeavesCount'))}
              className="text-center"
            >
              <AdherenceHistoryBadge displayValueAsWord={isOneDaySelected} kind="danger">
                {earlyLeavesCount}
              </AdherenceHistoryBadge>
            </td>
            <td>
              {/* Intentionally blank to fill the rest of the available space according to Adherence History table */}
            </td>
          </tr>
        </tbody>
      </Table>
      <div className="adherence-history__bottom-panel-corner">
        <Tooltip message={t('layout.action.close')} placement="bottom">
          <Button
            aria-label={t('layout.action.close')}
            className="bottom-panel__close-button"
            data-test-id={BOTTOM_PANEL_CLOSE_BUTTON}
            kind="info"
            onClick={handleClose}
          >
            <Icon color="red" kind="cross" size={12} />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
